/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Group } from '../models/Group';
import type { GroupRequest } from '../models/GroupRequest';
import type { PaginatedGroupList } from '../models/PaginatedGroupList';
import type { PatchedGroupRequest } from '../models/PatchedGroupRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class GroupsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * API endpoint to view and edit company groups
     * @returns PaginatedGroupList
     * @throws ApiError
     */
    public companiesGroupsList({
        parentCompany,
        limit,
        offset,
    }: {
        parentCompany: string,
        /**
         * Number of results to return per page.
         */
        limit?: number,
        /**
         * The initial index from which to return the results.
         */
        offset?: number,
    }): CancelablePromise<PaginatedGroupList> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/groups/',
            path: {
                'parent_company': parentCompany,
            },
            query: {
                'limit': limit,
                'offset': offset,
            },
        });
    }

    /**
     * API endpoint to view and edit company groups
     * @returns Group
     * @throws ApiError
     */
    public companiesGroupsCreate({
        parentCompany,
        requestBody,
    }: {
        parentCompany: string,
        requestBody: GroupRequest,
    }): CancelablePromise<Group> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/companies/{parent_company}/groups/',
            path: {
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company groups
     * @returns Group
     * @throws ApiError
     */
    public companiesGroupsRetrieve({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this group.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<Group> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/companies/{parent_company}/groups/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

    /**
     * API endpoint to view and edit company groups
     * @returns Group
     * @throws ApiError
     */
    public companiesGroupsUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this group.
         */
        id: string,
        parentCompany: string,
        requestBody: GroupRequest,
    }): CancelablePromise<Group> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/companies/{parent_company}/groups/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company groups
     * @returns Group
     * @throws ApiError
     */
    public companiesGroupsPartialUpdate({
        id,
        parentCompany,
        requestBody,
    }: {
        /**
         * A UUID string identifying this group.
         */
        id: string,
        parentCompany: string,
        requestBody?: PatchedGroupRequest,
    }): CancelablePromise<Group> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/companies/{parent_company}/groups/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * API endpoint to view and edit company groups
     * @returns void
     * @throws ApiError
     */
    public companiesGroupsDestroy({
        id,
        parentCompany,
    }: {
        /**
         * A UUID string identifying this group.
         */
        id: string,
        parentCompany: string,
    }): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/companies/{parent_company}/groups/{id}/',
            path: {
                'id': id,
                'parent_company': parentCompany,
            },
        });
    }

}
